import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, FileSpreadsheet, Timer } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { ReportMap } from "@/components/ReportMap";
import { Timeline } from "@/components/Timeline";
import { PeriodSummary } from "@/components/PeriodSummary";
import { DisciplineByTeam } from "@/components/DisciplineByTeam";
import { SubsByTeam } from "@/components/SubsByTeam";
import { PlayerSummary } from "@/components/PlayerSummary";
import { NotFound } from "@/components/NotFound";
import { getMatch } from "@/lib/api";
import { visibleTypes, scoreText } from "@/lib/events";
import { ShootoutSummary } from "@/components/ShootoutSummary";
import { ExpulsionsSummary } from "@/components/ExpulsionsSummary";
import { KeyMoments } from "@/components/KeyMoments";
import { exportCSV, exportPDF, countBy } from "@/lib/export";

const Summary = ({ match }) => (
  <table data-testid="report-summary" className="w-full text-sm">
    <thead>
      <tr className="label-caps text-left border-b border-white/15">
        <th className="py-3 font-bold">Evento</th>
        <th className="py-3 font-bold text-right">{match.home.name}</th>
        <th className="py-3 font-bold text-right">{match.away.name}</th>
        <th className="py-3 font-bold text-right">Sem equipa</th>
      </tr>
    </thead>
    <tbody>
      {!visibleTypes(match).length && <tr><td colSpan={4} data-testid="summary-empty" className="py-3 text-zinc-500">Sem eventos registados.</td></tr>}
      {visibleTypes(match).map((t) => (
        <tr key={t.key} className="border-b border-white/10">
          <td className="py-3 flex items-center gap-3"><span className="w-1.5 h-5" style={{ background: t.color }} />{t.label}</td>
          <td data-testid={`summary-home-${t.key}`} className="py-3 text-right font-display text-xl font-bold">{countBy(match, t.key, "home")}</td>
          <td data-testid={`summary-away-${t.key}`} className="py-3 text-right font-display text-xl font-bold">{countBy(match, t.key, "away")}</td>
          <td data-testid={`summary-none-${t.key}`} className="py-3 text-right font-display text-xl font-bold text-zinc-400">{countBy(match, t.key, null)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function Report() {
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  useEffect(() => { getMatch(id).then(setMatch).catch(() => setMatch(false)); }, [id]);
  if (match === false) return <NotFound />;
  if (!match) return null;

  return (
    <div className="relative z-10">
      <TopBar>
        <Link to={`/jogo/${id}`} data-testid="back-to-live-btn" className="tap h-10 px-3 border border-white/15 flex items-center gap-2 text-sm hover:bg-white/5">
          <Timer size={16} /> <span className="hidden sm:inline">Jogo</span>
        </Link>
        <button data-testid="export-csv-btn" onClick={() => exportCSV(match)} className="tap h-10 px-3 border border-white/15 flex items-center gap-2 text-sm hover:bg-white/5">
          <FileSpreadsheet size={16} /> CSV
        </button>
        <button data-testid="export-pdf-btn" onClick={() => exportPDF(match)} className="tap h-10 px-3 bg-[#CCFF00] text-black flex items-center gap-2 text-sm font-semibold hover:brightness-110">
          <FileText size={16} /> PDF
        </button>
      </TopBar>
      <main className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-14 space-y-8 sm:space-y-12">
        <div className="rise">
          <p className="label-caps mb-3">Relatório de jogo · {[match.competition, match.date, match.venue].filter(Boolean).join(" · ")}</p>
          <h1 data-testid="report-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase">
            {match.home.name} <span data-testid="report-score" className="text-[#CCFF00]">{scoreText(match, "–")}</span> {match.away.name}
          </h1>
        </div>
        <KeyMoments match={match} />
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 [&>*]:min-w-0">
          <ReportMap match={match} />
          <div className="border border-white/15 bg-[#131313] p-4 sm:p-6 overflow-x-auto"><Summary match={match} /></div>
        </div>
        {match.shootout?.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold uppercase mb-3">Desempate por penáltis</h2>
            <ShootoutSummary match={match} />
          </section>
        )}
        <section>
          <h2 className="font-display text-2xl font-bold uppercase mb-3">Disciplina e penáltis por equipa</h2>
          <DisciplineByTeam match={match} />
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold uppercase mb-3">Expulsões</h2>
          <ExpulsionsSummary match={match} />
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold uppercase mb-3">Resumo por jogador</h2>
          <PlayerSummary match={match} />
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold uppercase mb-3">Substituições por equipa</h2>
          <SubsByTeam match={match} />
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold uppercase mb-3">Resumo por períodos</h2>
          <PeriodSummary match={match} />
        </section>
        <details data-testid="detailed-timeline" className="group">
          <summary data-testid="detailed-timeline-toggle" className="cursor-pointer list-none font-display text-xl font-bold uppercase text-zinc-400 hover:text-white mb-3">
            <span className="group-open:hidden">+ Ver cronologia detalhada</span><span className="hidden group-open:inline">− Ocultar cronologia detalhada</span>
          </summary>
          <Timeline match={match} />
        </details>
      </main>
    </div>
  );
}
